import { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { Spin, Table } from "antd";
import { LeftOutlined, GlobalOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import type { ColumnsType } from "antd/es/table";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useScopeParams } from "@/hooks/useScopeParams";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { apiFetchJson } from "@/utils/apiFetch";
import type { Investigator } from "./types";

type GeoType = "country" | "region";

async function fetchPortfolioInvestigators(): Promise<Investigator[]> {
  return apiFetchJson<Investigator[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-investigators/`,
  );
}

const COLUMNS: ColumnsType<Investigator> = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
  },
  {
    title: "Specialty",
    dataIndex: "specialty",
    key: "specialty",
    sorter: (a, b) => (a.specialty ?? "").localeCompare(b.specialty ?? ""),
  },
  {
    title: "Institution",
    dataIndex: "institution",
    key: "institution",
  },
  {
    title: "City",
    dataIndex: "city",
    key: "city",
  },
  {
    title: "Trials",
    key: "trial_count",
    width: 80,
    render: (_: unknown, inv: Investigator) =>
      inv.trials?.length ?? inv.trial_count ?? 0,
    sorter: (a, b) =>
      (a.trials?.length ?? a.trial_count ?? 0) -
      (b.trials?.length ?? b.trial_count ?? 0),
  },
  {
    title: "Tier",
    dataIndex: "investigator_tier",
    key: "investigator_tier",
    width: 90,
    render: (v: string | null) => v ?? "—",
  },
  {
    title: "KOL",
    dataIndex: "is_kol",
    key: "is_kol",
    width: 60,
    render: (v: boolean | null) => (v ? "Yes" : "—"),
  },
];

export default function InvestigatorGeoProfile() {
  const { geoType, geoValue: encodedGeoValue } = useParams<{
    geoType: string;
    geoValue: string;
  }>();
  const { scope, id, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const profileHidden = useFeatureFlag(
    FEATURE_FLAGS.INVESTIGATOR_PROFILE_LINKS_HIDDEN,
  );

  const geoValue = encodedGeoValue ? decodeURIComponent(encodedGeoValue) : "";

  const { data: allInvestigators = [], isLoading } = useQuery({
    queryKey: ["portfolio-investigators", scope, id],
    queryFn: () => fetchPortfolioInvestigators(),
    enabled: !!scope && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const filteredInvestigators = useMemo(() => {
    if (!geoType || !geoValue) return [];
    if (geoType === "country")
      return allInvestigators.filter((i) => i.country === geoValue);
    if (geoType === "region")
      return allInvestigators.filter((i) => i.region === geoValue);
    return [];
  }, [geoType, geoValue, allInvestigators]);

  const trialCount = useMemo(() => {
    const ids = new Set(
      filteredInvestigators.flatMap((i) =>
        (i.trials ?? []).map((t) => t.trial_id),
      ),
    );
    return ids.size;
  }, [filteredInvestigators]);

  const kolCount = useMemo(
    () => filteredInvestigators.filter((i) => i.is_kol).length,
    [filteredInvestigators],
  );

  const countries = useMemo(() => {
    if (geoType === "region") {
      return [...new Set(filteredInvestigators.map((i) => i.country))].sort();
    }
    return [];
  }, [geoType, filteredInvestigators]);

  const regionLabel = useMemo(() => {
    if (geoType === "country" && filteredInvestigators.length > 0) {
      return filteredInvestigators[0].region;
    }
    return "";
  }, [geoType, filteredInvestigators]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!filteredInvestigators.length) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-[13px]">
        Geography not found.
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <div className="w-[260px] min-w-[260px] sticky top-0 self-start h-screen flex flex-col gap-2 p-3 pb-6 overflow-y-auto">
        <Link
          to={buildUrl("investigators")}
          className="flex items-center gap-1.5 text-[12px] text-[#556C75] hover:text-[#2B4752] px-1 py-1"
        >
          <LeftOutlined className="text-[10px]" />
          All Investigators
        </Link>

        <div
          className="px-3 py-3 flex flex-col gap-1"
          style={{
            borderRadius: 8,
            border: "1px solid rgba(0, 0, 0, 0.04)",
            background: "#FFF",
            boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center p-[5px] rounded-[4px] bg-[#2B4752] text-white">
              <GlobalOutlined style={{ fontSize: 20 }} />
            </div>
            <span className="text-[10px] font-semibold text-[#556C75] tracking-wide uppercase">
              {(geoType as GeoType) === "region" ? "Region" : "Country"}
            </span>
          </div>
          <span className="text-[15px] font-bold text-[#1F2933] leading-tight">
            {geoValue}
          </span>
          {geoType === "region" && countries.length > 0 && (
            <span className="text-[11px] text-[#8C8C8C]">
              {countries.join(", ")}
            </span>
          )}
          {geoType === "country" && regionLabel && (
            <span className="text-[11px] text-[#8C8C8C]">{regionLabel}</span>
          )}

          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#F0F2F4] flex-wrap">
            <div className="flex flex-col items-center">
              <span className="text-[16px] font-semibold text-[#1F2933]">
                {filteredInvestigators.length}
              </span>
              <span className="text-[10px] text-[#8C8C8C] uppercase tracking-wide">
                Investigators
              </span>
            </div>
            <div className="w-px h-8 bg-[#F0F2F4]" />
            <div className="flex flex-col items-center">
              <span className="text-[16px] font-semibold text-[#1F2933]">
                {trialCount}
              </span>
              <span className="text-[10px] text-[#8C8C8C] uppercase tracking-wide">
                Trials
              </span>
            </div>
            {kolCount > 0 && (
              <>
                <div className="w-px h-8 bg-[#F0F2F4]" />
                <div className="flex flex-col items-center">
                  <span className="text-[16px] font-semibold text-[#1F2933]">
                    {kolCount}
                  </span>
                  <span className="text-[10px] text-[#8C8C8C] uppercase tracking-wide">
                    KOLs
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Region link when viewing a country */}
        {geoType === "country" && regionLabel && (
          <button
            type="button"
            className="text-left text-[12px] text-[#0C98D0] hover:underline px-1"
            onClick={() =>
              navigate(
                buildUrl(
                  `investigators/geo/region/${encodeURIComponent(regionLabel)}`,
                ),
              )
            }
          >
            View {regionLabel} Region →
          </button>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 p-4 overflow-auto">
        <Table
          dataSource={filteredInvestigators}
          columns={COLUMNS}
          rowKey="inv_key"
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: false }}
          onRow={
            profileHidden
              ? undefined
              : (inv) => ({
                  onClick: () =>
                    navigate(buildUrl(`investigators/${inv.inv_key}`)),
                  style: { cursor: "pointer" },
                })
          }
        />
      </div>
    </div>
  );
}
